import { useEffect, useState } from 'react';
import { Button, DatePicker, Table, Space, message } from 'antd';
import dayjs from 'dayjs';
import { useSnapshot } from 'valtio';
import { archiveChannelStat, archiveChannelStatExport } from '@/api/activity/activity';
import { state } from '@/store';
import { TRACK_BOARD_KEY } from '@/router/Permissions';
import { checkPermissions } from '@/utils/utils';

const { RangePicker } = DatePicker;

const TrackBoard = () => {
  const snap = useSnapshot(state);
  const currentShopId = snap.currentShopId;
  const shopId =
    currentShopId || (typeof window !== 'undefined' ? Number(localStorage.getItem('currentShopId')) : null);

  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);

  const columns = [
    {
      title: '日期',
      dataIndex: 'statDate',
      key: 'statDate',
      width: 120,
      fixed: 'left' as const,
      render: (date: string) => {
        if (!date) return '-';
        const d = dayjs(date);
        return `${d.format('M月D日')}`;
      },
    },
    {
      title: '首页banner',
      dataIndex: 'archiveHomeCount',
      key: 'archiveHomeCount',
      width: 100,
      align: 'center' as const,
      render: (value: number) => value ?? 0,
    },
    {
      title: '详情页banner',
      dataIndex: 'archiveDetailCount',
      key: 'archiveDetailCount',
      width: 100,
      align: 'center' as const,
      render: (value: number) => value ?? 0,
    },
    {
      title: '会员页banner',
      dataIndex: 'archiveMemberCount',
      key: 'archiveMemberCount',
      width: 100,
      align: 'center' as const,
      render: (value: number) => value ?? 0,
    },
    {
      title: '客服链接',
      dataIndex: 'archiveCustomerCount',
      key: 'archiveCustomerCount',
      width: 100,
      align: 'center' as const,
      render: (value: number) => value ?? 0,
    },
    {
      title: '短信',
      dataIndex: 'archiveSmsCount',
      key: 'archiveSmsCount',
      width: 100,
      align: 'center' as const,
      render: (value: number) => value ?? 0,
    },
    {
      title: '首页弹窗',
      dataIndex: 'archiveHomePopup',
      key: 'archiveHomePopup',
      width: 100,
      align: 'center' as const,
      render: (value: number) => value ?? 0,
    },
    {
      title: '直播间',
      dataIndex: 'archiveLive',
      key: 'archiveLive',
      width: 100,
      align: 'center' as const,
      render: (value: number) => value ?? 0,
    },
    {
      title: 'DM单',
      dataIndex: 'archiveDm',
      key: 'archiveDm',
      width: 100,
      align: 'center' as const,
      render: (value: number) => value ?? 0,
    },
    {
      title: '会员咨询',
      dataIndex: 'archiveConsult',
      key: 'archiveConsult',
      width: 100,
      align: 'center' as const,
      render: (value: number) => value ?? 0,
    },
    {
      title: '备用1',
      dataIndex: 'archiveBackup1',
      key: 'archiveBackup1',
      width: 100,
      align: 'center' as const,
      render: (value: number) => value ?? 0,
    },
    {
      title: '备用2',
      dataIndex: 'archiveBackup2',
      key: 'archiveBackup2',
      width: 100,
      align: 'center' as const,
      render: (value: number) => value ?? 0,
    },
  ];

  const fetchData = async (page = 1, pageSize = 10) => {
    if (!shopId) {
      console.error('缺少店铺ID');
      return;
    }

    setLoading(true);
    try {
      const params: any = {
        shopId: String(shopId),
        pageNum: page,
        pageSize,
        orderByColumn: 'statDate',
        isAsc: 'desc',
      };

      // 只有日期不为空时才添加日期参数
      if (dateRange[0]) {
        params.startTime = dateRange[0].format('YYYY-MM-DD');
      }
      if (dateRange[1]) {
        params.endTime = dateRange[1].format('YYYY-MM-DD');
      }

      const res: any = await archiveChannelStat(params);

      if (res?.code === 200 || res?.code === 0) {
        setDataSource(res.rows || []);
        setPagination({
          current: page,
          pageSize,
          total: res.total || 0,
        });
      }
    } catch (error) {
      console.error('获取埋点数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, pagination.pageSize);
  }, []);

  const handleQuery = () => {
    fetchData(1, pagination.pageSize);
  };

  const handleReset = () => {
    setDateRange([null, null]);
    setPagination({ ...pagination, current: 1 });
    setTimeout(() => {
      fetchData(1, pagination.pageSize);
    }, 0);
  };

  const handleExport = async () => {
    if (!shopId) {
      message.warning('请先选择店铺');
      return;
    }

    // 构建导出参数（不包含分页参数）
    const params: any = {
      shopId: String(shopId),
      orderByColumn: 'statDate',
      isAsc: 'desc',
    };

    // 只有日期不为空时才添加日期参数
    if (dateRange[0]) {
      params.startTime = dateRange[0].format('YYYY-MM-DD');
    }
    if (dateRange[1]) {
      params.endTime = dateRange[1].format('YYYY-MM-DD');
    }

    try {
      await archiveChannelStatExport(params);
    } catch (error) {
      console.error('导出失败:', error);
    }
  };

  const handleTableChange = (paginationInfo: any) => {
    fetchData(paginationInfo.current, paginationInfo.pageSize);
  };

  return (
    <div style={{ padding: 24 }}>
      {/* 页面标题 */}
      <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>埋点数据看板</div>

      {/* 筛选条件表单 */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space size={12}>
          <div>
            <span style={{ marginRight: 8 }}>开始日期：</span>
            <DatePicker
              value={dateRange[0]}
              onChange={date => {
                setDateRange([date, dateRange[1]]);
              }}
              format="YYYY/MM/DD"
              allowClear
              placeholder="请选择开始日期"
              style={{ width: 200 }}
            />
          </div>
          <div>
            <span style={{ marginRight: 8 }}>结束日期：</span>
            <DatePicker
              value={dateRange[1]}
              onChange={date => {
                setDateRange([dateRange[0], date]);
              }}
              format="YYYY/MM/DD"
              allowClear
              placeholder="请选择结束日期"
              disabledDate={current => {
                return !!(current && dateRange[0] && current < dateRange[0].endOf('day'));
              }}
              style={{ width: 200 }}
            />
          </div>
          <Button
            type="primary"
            onClick={handleQuery}>
            查询
          </Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
        {checkPermissions(TRACK_BOARD_KEY.EXPORT) && (
          <Button
            type="primary"
            onClick={handleExport}>
            导出
          </Button>
        )}
      </div>

      {/* 数据表格 */}
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        bordered
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: total => `共 ${total} 条`,
          onChange: (page, pageSize) => {
            setPagination({ ...pagination, current: page, pageSize });
            handleTableChange({ current: page, pageSize });
          },
        }}
        rowKey={record => record.statDate || Math.random()}
        scroll={{ x: 1200 }}
      />
    </div>
  );
};

export default TrackBoard;
